"use strict";

var EventEmitter = require("events").EventEmitter;
var LoggerInstance = require("js-logger").get("Store");

var ProjectConstants = require("./ProjectConstants");

var WizardConstants = require("../constants/WizardConstants");

var Dispatcher = require("../dispatcher/Dispatcher");

var AbstractStore = require("../stores/AbstractStore");

var CHANGE_EVENT = "change";

var state = {
	arePaymentTermsConfigured: false,
	defaultPledgeFrequency: null,
	isAnonymous: false,
	isDesignMode: false,
	isInPledgeStatementSession: false,
	isSecure: false,
	minimumAmount: null,
	minimumAmounts: {},
	project: null,
	pledgeId: null,
	relatedAccount: null,
	selectedAmount: null,
	selectedAmounts: {},
	selectedFrequency: null,
	selectedFrequencies: {},
	selectedPremium: null,
	selectedPremiums: {},
	selectedStartDate: null,
	selectedStartDates: {},
	selectedUpsells: {},
	selectedUseOnDay: null,
	selectedUseOnDays: {},
	subaccountAmounts: {},
	upsell: false,
	wizardCheckoutDisplayMessages: {},
	wizardDefaultUseOnDay: {},
	wizardFrequencies: {},
	wizardPledgeStatementEntry: {},
	wizardProjectCompletionDefaults: {},
	wizardRecurringStartDateDropdownValues: {},
	wizardSecureProjects: {},
	wizardShoppingCartDisplayMessages: {},
	wizardSubaccounts: {},
	wizardUseOnDays: {}
};

var ProjectStore = class extends AbstractStore {
	constructor(dispatcher) {
		super();
		this.dispatchToken = dispatcher.register(action => {
			// Use to determine which pledge we're associated with, if any
			var { tangibleCode, pledgeCode } = action;

			switch (action.actionType) {
				case ProjectConstants.SET_GIFT_AMOUNT:
					if (action.context) {
						if (!state.selectedAmounts[action.context]) {
							state.selectedAmounts[action.context] = {};
						}
						state.selectedAmounts[action.context][action.tangibleCode] = action.selectedAmount;
					} else {
						state.selectedAmount = action.selectedAmount;
					}
					break;

				case ProjectConstants.SET_MINIMUM_AMOUNT:
					if (action.context) {
						if (!state.minimumAmounts[action.context]) {
							state.minimumAmounts[action.context] = {};
						}
						state.minimumAmounts[action.context][action.tangibleCode] = action.minimumAmount;
					} else {
						state.minimumAmount = action.minimumAmount;
					}
					break;

				case ProjectConstants.SET_SUBACCOUNT_GIFT_AMOUNT:
					state.subaccountAmounts[action.subaccountCode] = action.amount;
					break;

				case ProjectConstants.SET_DONATION_FREQUENCY:
					if (pledgeCode) {
						tangibleCode = `${tangibleCode}_${pledgeCode}`;
					}

					if (action.context) {
						if (!state.selectedFrequencies[action.context]) {
							state.selectedFrequencies[action.context] = {};
						}
						state.selectedFrequencies[action.context][tangibleCode] = action.selectedFrequency;
					} else {
						state.selectedFrequency = action.selectedFrequency;
					}
					break;

				case ProjectConstants.SET_DONATION_PREMIUM:
					if (action.context) {
						if (!state.selectedPremiums[action.context]) {
							state.selectedPremiums[action.context] = {};
						}
						state.selectedPremiums[action.context][action.tangibleCode] = action.selectedPremium;
					} else {
						state.selectedPremium = action.selectedPremium;
					}
					break;

				case ProjectConstants.SET_RECURRING_START_DATE:
					if (action.context) {
						if (!state.selectedStartDates[action.context]) {
							state.selectedStartDates[action.context] = {};
						}
						state.selectedStartDates[action.context][action.tangibleCode] = action.selectedStartDate;
					} else {
						state.selectedStartDate = action.selectedStartDate;
					}
					break;

				case ProjectConstants.SET_USE_ON_DAY:
					if (pledgeCode) {
						tangibleCode = `${tangibleCode}_${pledgeCode}`;
					}

					if (action.context) {
						if (!state.selectedUseOnDays[action.context]) {
							state.selectedUseOnDays[action.context] = {};
						}
						state.selectedUseOnDays[action.context][tangibleCode] = action.selectedUseOnDay;
					} else {
						state.selectedUseOnDay = action.selectedUseOnDay;
					}
					break;

				case ProjectConstants.SET_UPSELL:
					if (action.context) {
						var { tangibleCode, pledgeCode } = action;
						if (pledgeCode) {
							tangibleCode = `${tangibleCode}_${pledgeCode}`;
						}

						if (!state.selectedUpsells[action.context]) {
							state.selectedUpsells[action.context] = {};
						}
						state.selectedUpsells[action.context][tangibleCode] = action.upsell;
					} else {
						state.upsell = action.upsell;
					}
					break;

				case ProjectConstants.SET_ANONYMOUS:
					state.isAnonymous = action.isAnonymous;
					break;

				case ProjectConstants.INITIALIZE:
					state.arePaymentTermsConfigured = action.options.arePaymentTermsConfigured;
					state.defaultPledgeFrequency = action.options.defaultPledgeFrequency;
					state.isDesignMode = action.options.isDesignMode;
					state.isInPledgeStatementSession = action.options.isInPledgeStatementSession;
					state.project = action.options.project;
					state.wizardCheckoutDisplayMessages = action.options.wizardCheckoutDisplayMessages;
					state.wizardDefaultUseOnDay = action.options.wizardDefaultUseOnDay;
					state.wizardFrequencies = action.options.wizardFrequencies;
					state.wizardPledgeStatementEntry = action.options.wizardPledgeStatementEntry;
					state.wizardProjectCompletionDefaults = action.options.wizardProjectCompletionDefaults;
					state.wizardRecurringStartDateDropdownValues = action.options.wizardRecurringStartDateDropdownValues;
					state.wizardShoppingCartDisplayMessages = action.options.wizardShoppingCartDisplayMessages;
					state.wizardSubaccounts = action.options.wizardSubaccounts;
					state.wizardUseOnDays = action.options.wizardUseOnDays;
					return true; // Don't emit change

				default:
					// no op
					LoggerInstance.debug(`Undefined action triggered in project store. ${action.actionType}`);
					return true;
			};

			this.emitChange();
			return true;
		});
	}

	donationStartsToday(context, tangibleCode) {
		return this
			.getCalculatedStartDate(context, tangibleCode)
			.isSame(new Date(), "day");
	}

	getState() {
		return state;
	}

	get(key) {
		return state[key];
	}

	// This is only used to determine if we're showing the recurring upsell checkbox. It uses
	// the calculated date from combining the start date (month and year) and the use on day,
	// and is not necessarily an accurate reflection of the actual start date that will be
	// assigned to the recurring.
	//
	// Returns a moment instance.
	getCalculatedStartDate(context, tangible, pledgeCode) {
		var today = window.moment();
		var startDate = this.getSelectedStartDate(context, tangible);
		var useOnDay = this.getSelectedUseOnDay(context, tangible, pledgeCode);

		// If we don't have an assiged start date, use today
		if (!startDate) {
			startDate = today;
		} else {
			startDate = window.moment(startDate, "MM-YYYY");
		}

		// set the day to the selected use on day
		useOnDay = Math.min(useOnDay, startDate.daysInMonth());
		startDate.date(useOnDay);

		return startDate;
	}

	getArePaymentTermsConfigured() {
		return state.arePaymentTermsConfigured;
	}

	getIsDesignMode() {
		return state.isDesignMode;
	}

	getIsSecure() {
		return state.isSecure;
	}

	setIsSecure(isSecure) {
		state.isSecure = isSecure;
	}

	setSecureProjectsWizard(secureProjectsWizard) {
		state.wizardSecureProjects = secureProjectsWizard;
	}

	getPledgeId() {
		return state.pledgeId;
	}

	setPledgeId(pledgeId) {
		state.pledgeId = pledgeId;
	}

	getRelatedAccount() {
		return state.relatedAccount;
	}

	setRelatedAccount(relatedAccount) {
		state.relatedAccount = relatedAccount;
	}

	getMinimumAmount(context, tangibleCode) {
		if (context) {
			if (state.minimumAmounts[context]) {
				return state.minimumAmounts[context][tangibleCode];
			}

			return null;
		}

		return state.minimumAmount;
	}

	getSelectedAmount(context, tangibleCode) {
		if (context) {
			if (state.selectedAmounts[context]) {
				return state.selectedAmounts[context][tangibleCode];
			} else {
				return null;
			}
		} else {
			return state.selectedAmount;
		}
	}

	getSelectedFrequency(context, tangibleCode, pledgeCode) {
		var selectedFrequency = state.selectedFrequency;
		var defaultFrequency = state.wizardProjectCompletionDefaults.defaultFrequency || "O";

		if (pledgeCode) {
			tangibleCode = `${tangibleCode}_${pledgeCode}`;
			defaultFrequency = state.defaultPledgeFrequency;
		}

		if (context && state.selectedFrequencies[context]) {
			selectedFrequency = state.selectedFrequencies[context][tangibleCode];
		}

		return selectedFrequency || defaultFrequency;
	}

	getSelectedPremium(context, tangibleCode) {
		if (context) {
			if (state.selectedPremiums[context]) {
				return state.selectedPremiums[context][tangibleCode];
			} else {
				return null;
			}
		} else {
			return state.selectedPremium;
		}
	}

	getSelectedStartDate(context, tangibleCode) {
		if (context) {
			if (state.selectedStartDates[context]) {
				return state.selectedStartDates[context][tangibleCode];
			}

			return null;
		}

		return state.selectedStartDate;
	}

	getSelectedUseOnDay(context, tangibleCode, pledgeCode) {
		if (pledgeCode) {
			tangibleCode = `${tangibleCode}_${pledgeCode}`;
		}

		if (context) {
			if (state.selectedUseOnDays[context]) {
				return state.selectedUseOnDays[context][tangibleCode];
			} else {
				return null;
			}
		} else {
			return state.selectedUseOnDay;
		}
	}

	getSelectedUpsell(context, tangibleCode, pledgeCode) {
		if (context) {
			if (pledgeCode) {
				tangibleCode = `${tangibleCode}_${pledgeCode}`;
			}

			if (state.selectedUpsells[context]) {
				return state.selectedUpsells[context][tangibleCode];
			} else {
				return null;
			}
		} else {
			return state.upsell;
		}
	}

	getSubaccountAmounts() {
		return Object.keys(state.subaccountAmounts).length
			? state.subaccountAmounts
			: null;
	}

	getFrequencyDescription(frequency) {
		return state.wizardFrequencies[`${frequency}_Description`] || frequency;
	}

	getIsChargeTodayRequired() {
		return state.wizardProjectCompletionDefaults.requireDonation === WizardConstants.PROJECT_COMPLETION_DEFAULTS_REQUIRE_DONATION_VALUES.YES;
	}

	getIsChargeTodayRequiredOnlyWithPremium() {
		return state.wizardProjectCompletionDefaults.requireDonation === WizardConstants.PROJECT_COMPLETION_DEFAULTS_REQUIRE_DONATION_VALUES.WITH_PREMIUM;
	}

	getDonationInformation(cartDataOptions) {
		cartDataOptions = cartDataOptions || {};
		var { context, tangibleCode } = cartDataOptions;

		if (state.isSecure) {
			tangibleCode = state.wizardSecureProjects.projectCode;
		}

		var donationInformation = {
			// Because the project information is pulled from the Tangibles table populated from SE, we cannot properly camelCase keys.
			// We are using the bracket notation to indicate that this is intended
			project: tangibleCode || state.project["ProjectCode"],
			amount: this.getSelectedAmount(context, tangibleCode),
			subaccountAmounts: context ? null : state.subaccountAmounts,
			frequency: this.getSelectedFrequency(context, tangibleCode),
			anonymous: context ? false : state.isAnonymous,
			useOnDay: this.getSelectedUseOnDay(context, tangibleCode),
			upsell: false,
			premium: this.getSelectedPremium(context, tangibleCode),
			startDate: this.getSelectedStartDate(context, tangibleCode),
			minimumDonationAmount: this.getMinimumAmount(context, tangibleCode)
		};
		donationInformation.isRecurring = donationInformation.frequency !== "O";

		if (!donationInformation.useOnDay && donationInformation.isRecurring && state.wizardDefaultUseOnDay && state.wizardDefaultUseOnDay.defaultUseOnDay) {
			donationInformation.useOnDay = state.wizardDefaultUseOnDay.defaultUseOnDay;
		} else if (!donationInformation.isRecurring) {
			donationInformation.useOnDay = null;
		}

		if (donationInformation.frequency !== "O" && !this.donationStartsToday(context, tangibleCode)) {
			// If the frequency is not "O", where we explicitly charge today no matter what,
			// or if the donation does not start exactly today then we get the value from what the user selected.
			// Otherwise the component is hidden, and upsell is false.
			donationInformation.upsell = this.getSelectedUpsell(context, tangibleCode);
		}

		if (!donationInformation.upsell && (this.getIsChargeTodayRequired() || (donationInformation.premium && this.getIsChargeTodayRequiredOnlyWithPremium))) {
			donationInformation.upsell = true;
		}

		// Donations toward a Secure Pledge will set state.pledgeId and possibly state.relatedAccount when the Secure Project Page loads.
		if (state.isSecure) {
			if (state.pledgeId) {
				donationInformation.pledgeId = state.pledgeId;
			}

			if (state.relatedAccount) {
				donationInformation.relatedAccount = state.relatedAccount;
			}
		}

		return donationInformation;
	}

	getProjectDispatcher() {
		return Dispatcher;
	}
};

module.exports = new ProjectStore(Dispatcher);
